import { create } from 'zustand'
import { calculateAvailableOptions } from './calculateAvailableOptions'
import applyStatementHighlight from '../components/StatementFilterPanel/highlightStatements'
import filterStatements from '../components/StatementFilterPanel/filterStatements'
import {
    saveStoredStopWords,
    saveStoredPinnedNodes,
} from '../utils/selectionStorage'
import { getModeFromArray } from '../hooks/utils'
import { useGraphStore } from './useGraphStore'
import { useZustand } from '../hooks/useZustand'
import {
    useContextsStore,
    findContextByName,
} from './useContextsStore'
import { runGraphRecalculation } from '../utils/recalculateGraphFlow'
import { formatTopicsFromClusters } from '../utils/graphTopics'
import { buildRecalculatedFilterStatement } from '../utils/statementNormalizer'
import { useBootstrapStore } from './useBootstrapStore'
import {
    resolveInitialStopWords,
    resolveInitialPinnedNodes,
} from './bootstrap/urlHelpers'
// useFindStore lives downstream of this store at runtime (its setters call
// runGraphRecalculation through here when the user types). Static-importing
// it is safe because we only read .getState() inside an action handler — the
// module is fully evaluated by then.
import { useFindStore } from './useFindStore'

export const emptyState = {
    sentiments: [],
    tags: [],
    topics: [],
    overlapMode: false,
}

const filterKeys = Object.keys(emptyState)

const initialStopWordsValue = resolveInitialStopWords()
const initialPinnedNodesValue = resolveInitialPinnedNodes()

const initialState = {
    filterMode: 'highlight',
    recalculate: {
        ...emptyState,
    },
    highlight: {
        ...emptyState,
    },
    initialStopWords: initialStopWordsValue,
    stopWords: initialStopWordsValue,
    // Snapshot of the stopWords list at the time of the last recalc.
    // setRecalculate compares this against the current stopWords to detect
    // "stopwords got less restrictive" (a removed word, or shorter list),
    // which means previously-filtered statements may need to come back —
    // so we re-derive from the full statements set instead of the cached
    // filtered subset. Seeded to initial stopWords because URL/storage
    // stopWords are already applied by the bootstrap graph load.
    appliedStopWords: initialStopWordsValue,
    pinnedNodes: initialPinnedNodesValue,
    originalTopics: null,
    // Context filter for synthesis view — holds the contextId (numeric) to
    // filter statements by their source graph. Set via context node clicks.
    selectedContextId: null,

    availableOptions: {
        sentiments: ['Positive', 'Negative', 'Neutral'],
        tags: [],
        topics: [],
    },
    filteredStatements: [],
    statements: [],
    // Track currently selected statement IDs
    selectedStatements: new Set(),
}
const isFilterEmpty = filter => {
    return (
        filter.sentiments.length === 0 &&
        filter.tags.length === 0 &&
        filter.topics.length === 0
    )
}

const updateStatementCommunities = allStatements => {
    const communitiesToStatements = {}
    const statementsList = allStatements

    const statementsWithMetaData = statementsList.map(statement => {
        // Get hashtags for the statement - should come from API now
        const hashtags = statement.hashtags || []

        // Calculate top community for the statement using new graph store
        const statementCommunities = []
        if (hashtags && hashtags.length > 0) {
            hashtags.forEach(hashtag => {
                const word = hashtag.startsWith('#')
                    ? hashtag.slice(1)
                    : hashtag
                // Use compatibility layer (works with both new store and old graphFactory)
                const community = useGraphStore
                    .getState()
                    .getCommunityOfNode(word)
                if (community !== undefined) {
                    if (!communitiesToStatements[community]) {
                        communitiesToStatements[community] = []
                    }
                    communitiesToStatements[community].push(statement.uid)

                    statementCommunities.push(community)
                }
            })
        }

        // Get the most frequent community using getModeFromArray

        const topStatementCommunity =
            statementCommunities.length > 0
                ? getModeFromArray(statementCommunities)
                : null

        // The most frequent community for the statement doesn't mean this statement is the top one for this community
        // So after we get data from the graph we add topOfCommunity that signifies whether this statement is the top one for any of the communities

        return {
            ...statement,
            hashtags: hashtags,
            topCommunity: topStatementCommunity,
        }
    })

    const communitiesToTopStatements = {}

    Object.keys(communitiesToStatements).forEach(community => {
        const statements = communitiesToStatements[community]
        const topStatementOfCommunity = getModeFromArray(statements)
        communitiesToTopStatements[community] = topStatementOfCommunity
    })

    const statementsWithTopOfCommunityData = statementsWithMetaData.map(
        statement => {
            const topOfCommunity = Object.keys(communitiesToTopStatements).find(
                community =>
                    communitiesToTopStatements[community] === statement.uid
            )

            if (topOfCommunity != undefined) {
                return {
                    ...statement,
                    topOfCommunity: parseInt(topOfCommunity.toString()),
                }
            } else {
                return {
                    ...statement,
                    topOfCommunity: 'none', // not a valid community
                }
            }
        }
    )

    return statementsWithTopOfCommunityData
}

export const useFilterStore = create((set, get) => ({
    ...initialState,

    setFilterMode: mode => set({ filterMode: mode }),
    setStopWords: async stopWords => {
        const currentStopWords = get().stopWords || []
        const newStopWords = stopWords || []

        const isIdentical =
            newStopWords.length === currentStopWords.length &&
            newStopWords.every(word => currentStopWords.includes(word))

        if (isIdentical) {
            return
        }

        // If a context node is being added as a stopword and its filter
        // is active, clear the filter — the context's statements will be
        // removed from the graph so the filter would match nothing.
        const selectedContextId = get().selectedContextId
        if (selectedContextId != null) {
            const contextsState = useContextsStore.getState()
            const addedStopWords = newStopWords.filter(
                w => !currentStopWords.includes(w)
            )
            const removedContextEntry = addedStopWords.find(sw => {
                if (!sw.startsWith('context: ')) return false
                const ctxName = sw.replace('context: ', '')
                const entry = findContextByName(contextsState, ctxName)
                return entry && entry.id === selectedContextId
            })
            if (removedContextEntry) {
                set({ selectedContextId: null })
            }
        }

        set({ stopWords: newStopWords })
        saveStoredStopWords(newStopWords)
        // setRecalculate detects whether stopWords got less restrictive by
        // comparing the new list against `appliedStopWords` itself — no need
        // to forward a behavior flag from here.
        await get().setRecalculate(get().recalculate)
    },
    addStopWords: words => {
        const current = get().stopWords || []
        const toAdd = Array.isArray(words) ? words : [words]
        const updated = [...new Set([...current, ...toAdd])]
        get().setStopWords(updated)
        return updated
    },
    createRecalculationCallbacks: ({
        newState,
        statementWithUpdatedCommunities,
        asBaseline = false,
    }) => {
        // TODO: refactor this into separate functions
        let statementsForOptions = statementWithUpdatedCommunities
        let graphDataSnapshot = null

        const onGraphData = data => {
            const newTopics =
                data?.entriesAndGraphOfContext?.graph?.graphologyGraph
                    ?.attributes?.top_clusters || []

            if (asBaseline) {
                // Graph first so setTopics → deriveInsightClusters reads fresh gaps.
                useGraphStore.getState().setGraphData(data)
                // setGraphData clones, so re-capture the stored ref to keep the
                // onTopicsApplied guard matching (bails when a later recalc replaces it).
                graphDataSnapshot = useGraphStore.getState().graphData

                if (newTopics.length > 0) {
                    const formattedTopics = formatTopicsFromClusters(newTopics)
                    useZustand.getState().setTopics(formattedTopics, true)
                }
            } else {
                graphDataSnapshot = data
                // Graph first so setTopics → deriveInsightClusters reads the
                // recalculated graph's gaps (same rule as the baseline branch
                // above; previously setTopics ran first and derived
                // insightClusters from the PREVIOUS graph's gaps until the
                // AI topic names arrived and re-ran setTopics).
                useGraphStore
                    .getState()
                    .setRecalculatedGraphData(data, data.filterMetadata)
                if (newTopics.length > 0) {
                    const savedOriginalTopics = get().originalTopics
                    if (!savedOriginalTopics) {
                        const currentTopics = useZustand.getState().topics || []
                        set({ originalTopics: currentTopics })
                    }
                    const formattedTopics = formatTopicsFromClusters(newTopics)
                    useZustand.getState().setTopics(formattedTopics, true)
                }
            }

            const recalculatedStatements =
                data?.entriesAndGraphOfContext?.statements || []

            if (recalculatedStatements.length > 0) {
                // Each backend statement becomes one filter-store statement.
                // If we already have a local copy (keyed by id), its local-
                // only fields — notably the client-side `sentiment` set
                // during bootstrap — are preserved on top of the fresh
                // backend data. If not, the statement was newly revealed by
                // the recalc (e.g. a `context: X` stopword was removed) and
                // is built purely from backend data; it won't have sentiment
                // until the next bootstrap.
                //
                // Field mapping lives in `buildRecalculatedFilterStatement`
                // so both paths (merge-with-local and new-from-backend) stay
                // in lockstep with `useGraphStore.getStatements`.
                // TODO: fix this, sentiment should be built in backend!
                const localById = new Map(
                    statementWithUpdatedCommunities.map(s => [String(s.uid), s])
                )

                const updatedStatementsWithRecalc = recalculatedStatements.map(
                    recalculated =>
                        buildRecalculatedFilterStatement(
                            recalculated,
                            localById.get(String(recalculated.id))
                        )
                )

                statementsForOptions = updatedStatementsWithRecalc
                const refreshedOptions = get().recalculateAvailableOptions(
                    updatedStatementsWithRecalc,
                    newState
                )
                // Re-apply highlight/pin/context state on top of the recalc'd
                // set. buildRecalculatedFilterStatement clears grayedOut as a
                // baseline ("passes the graph filter"), but pin/context/topic
                // dimming and pinnedRanking metadata must survive a recalc.
                const highlightedStatements = applyStatementHighlight(
                    get().highlight,
                    updatedStatementsWithRecalc,
                    get().pinnedNodes,
                    get().selectedContextId
                )
                set({
                    filteredStatements: highlightedStatements,
                    availableOptions: refreshedOptions,
                })
            }
        }

        const onTopicsApplied = (graphWithTopics, topicsExtension) => {
            if (!topicsExtension) {
                return
            }

            const currentGraphData = useGraphStore.getState().graphData
            if (currentGraphData !== graphDataSnapshot) {
                return
            }

            // only update topics in the local without updating the graph
            // useGraphStore.getState().applyTopicsExtension(topicsExtension)

            const updatedTopics =
                graphWithTopics?.entriesAndGraphOfContext?.graph
                    ?.graphologyGraph?.attributes?.top_clusters || []

            if (updatedTopics.length > 0) {
                const formattedTopics = formatTopicsFromClusters(updatedTopics)

                useZustand.getState().setTopics(formattedTopics, true)

                const currentRecalculate = get().recalculate
                const updatedSelectedTopics =
                    currentRecalculate.topics?.length > 0
                        ? currentRecalculate.topics.map(topic => {
                              const matched = formattedTopics.find(
                                  entry => entry.id === topic.id
                              )
                              if (!matched) return topic
                              return {
                                  ...matched,
                                  aiName: topic.aiName,
                                  nodes: topic.nodes,
                              }
                          })
                        : currentRecalculate.topics
                const updatedRecalculate = {
                    ...currentRecalculate,
                    topics: updatedSelectedTopics,
                }
                const refreshedOptions = get().recalculateAvailableOptions(
                    statementsForOptions,
                    updatedRecalculate
                )
                set({
                    recalculate: updatedRecalculate,
                    availableOptions: refreshedOptions,
                })
            }
        }

        return { onGraphData, onTopicsApplied }
    },
    setRecalculate: async recalc => {
        const prevState = get().recalculate
        const newState = { ...prevState, ...recalc }

        const valuesRemoved = filterKeys.some(key => {
            if (Array.isArray(get().recalculate[key]) && recalc[key]) {
                const prev = get().recalculate[key]
                const next = recalc[key]
                // A value got removed if the list is shorter, OR a
                // previously-applied value is no longer present (a swap, e.g.
                // single-select sentiment going Negative -> Positive at the
                // same length). In both cases the cached filtered subset is
                // missing statements that should come back, so we must
                // re-derive from the full statements set.
                return (
                    prev.length > next.length ||
                    prev.some(value => !next.includes(value))
                )
            }
            return false
        })

        const stopWords = get().stopWords || []
        const appliedStopWords = get().appliedStopWords || []
        // Stopwords got less restrictive if the list is now shorter, OR
        // some previously-applied word is no longer present (a swap).
        // In either case, the filtered subset is missing statements that
        // should come back, so we re-derive from the full set.
        const stopWordsReduced =
            stopWords.length < appliedStopWords.length ||
            appliedStopWords.some(word => !stopWords.includes(word))
        const initialStopWords = get().initialStopWords || []
        const noActiveFilters =
            isFilterEmpty(newState) && stopWords.length === 0

        if (noActiveFilters && initialStopWords.length === 0) {
            // resetAllFilters restores the original graph FIRST and then the
            // original topics (graph-first ordering rule). Calling
            // restoreOriginalTopics here beforehand would consume
            // originalTopics while the filtered graph is still active,
            // deriving insightClusters from the wrong gaps.
            get().resetAllFilters()
            return
        }
        // Initial stopwords need a real recalc, not reset-to-baseline.
        const shouldRecalculateClearedInitialStopWords =
            noActiveFilters && initialStopWords.length > 0

        if (shouldRecalculateClearedInitialStopWords) {
            set({ initialStopWords: [] })
        }

        let statements = get().filteredStatements
        if (
            valuesRemoved ||
            shouldRecalculateClearedInitialStopWords ||
            stopWordsReduced
        ) {
            statements = get().statements
        }

        const filteredStatements = filterStatements(
            prevState,
            newState,
            statements
        )

        // TODO: @oleg is still neeeded??
        // maybe for temporary recalculation
        const statementWithUpdatedCommunities = updateStatementCommunities(
            filteredStatements
        )

        const availableOptions = get().recalculateAvailableOptions(
            statementWithUpdatedCommunities,
            newState
        )
        set(state => ({
            recalculate: { ...state.recalculate, ...recalc },
            availableOptions,
            highlight: {
                ...emptyState,
            },
            filteredStatements: statementWithUpdatedCommunities,
        }))

        // Trigger graph recalculation if we have active recalculate filters or stopWords
        if (
            !isFilterEmpty(newState) ||
            stopWords.length > 0 ||
            shouldRecalculateClearedInitialStopWords
        ) {
            const currentContext = useZustand.getState().currentContext

            // Only forward statement IDs when topic/tag/sentiment filters are
            // active. Stopwords-only changes leave statementIds empty —
            // infrasonic resolves them via processing settings.
            const hasStatementLevelFilters = !isFilterEmpty(newState)
            const filteredStatementIds = hasStatementLevelFilters
                ? statementWithUpdatedCommunities.map(s => s.uid)
                : []

            const {
                isPublic,
                graphOwnerUsername,
                synthesisType,
                viewType,
            } = useBootstrapStore.getState()

            const isSearchView = viewType === 'search'
            const searchTerms = isSearchView
                ? useFindStore.getState().terms
                : null

            const additionalContexts =
                useZustand.getState().additionalContexts || []
            const comparisonMode = useZustand.getState().currentCompareMode

            const canDispatch =
                currentContext ||
                synthesisType ||
                (isSearchView && searchTerms?.length > 0)

            if (canDispatch) {
                try {
                    useGraphStore.getState().setRecalculating(true)

                    const {
                        onGraphData,
                        onTopicsApplied,
                    } = get().createRecalculationCallbacks({
                        newState,
                        statementWithUpdatedCommunities: statementWithUpdatedCommunities,
                    })

                    await runGraphRecalculation({
                        contextName:
                            synthesisType || isSearchView
                                ? null
                                : currentContext,
                        filteredStatementIds,
                        stopWords,
                        userName: isPublic ? graphOwnerUsername : null,
                        synthesisType,
                        searchTerms,
                        additionalContexts,
                        comparisonMode,
                        onGraphData,
                        onTopicsApplied,
                    })

                    // Advance the baseline only after the recalc succeeded;
                    // a failed fetch must leave pending stopwords still
                    // "unapplied" for the next setRecalculate.
                    set({ appliedStopWords: stopWords })
                } catch (error) {
                    console.error(
                        '[useFilterStore] Recalculation failed:',
                        error
                    )
                    useGraphStore.getState().setRecalculating(false)
                }
            }
        } else {
            // Reset to original graph when no recalculate filters.
            // The active graph has no stopwords applied, so the baseline
            // must reflect that — otherwise the next setRecalculate would
            // wrongly conclude "stopwords got less restrictive" and
            // re-derive from the full set.
            // Graph first so restoreOriginalTopics → deriveInsightClusters
            // reads the original graph's gaps.
            useGraphStore.getState().resetToOriginalGraph()
            get().restoreOriginalTopics()
            set({ appliedStopWords: [] })
        }
    },
    changeOverlap: filterMode => {
        const filter =
            filterMode === 'recalculate' ? get().recalculate : get().highlight
        const newFilter = {
            ...filter,
            overlapMode: !filter.overlapMode,
        }

        if (filterMode === 'recalculate') {
            const filteredStatements = filterStatements(
                filter,
                newFilter,
                get().statements
            )
            const statementWithUpdatedCommunities = updateStatementCommunities(
                filteredStatements
            )

            const availableOptions = get().recalculateAvailableOptions(
                statementWithUpdatedCommunities,
                newFilter
            )
            return set(state => ({
                recalculate: newFilter,
                availableOptions,
                highlight: {
                    ...emptyState,
                },
                filteredStatements: statementWithUpdatedCommunities,
            }))
        } else {
            const highlightedStatements = applyStatementHighlight(
                newFilter,
                get().filteredStatements,
                get().pinnedNodes,
                get().selectedContextId
            )

            return set(state => ({
                highlight: newFilter,
                filterMode: state.isAnyRecalculateActive()
                    ? state.filterMode
                    : 'highlight',
                filteredStatements: highlightedStatements,
            }))
        }
    },
    setHighlight: highlight => {
        const newState = { ...get().highlight, ...highlight }
        const highlightedStatements = applyStatementHighlight(
            newState,
            get().filteredStatements,
            get().pinnedNodes,
            get().selectedContextId
        )

        return set(state => ({
            highlight: { ...state.highlight, ...highlight },
            filterMode: state.isAnyRecalculateActive()
                ? state.filterMode
                : 'highlight',
            filteredStatements: highlightedStatements,
        }))
    },
    setPinnedNodes: nodes => {
        const newNodes = nodes || []
        const highlightedStatements = applyStatementHighlight(
            get().highlight,
            get().filteredStatements,
            newNodes,
            get().selectedContextId
        )
        saveStoredPinnedNodes(newNodes)
        return set({
            pinnedNodes: newNodes,
            filteredStatements: highlightedStatements,
        })
    },
    resetPinnedNodes: () => {
        if (window.socket) {
            window.socket.emit('graph reset')
        }
        saveStoredPinnedNodes([])
        set({ pinnedNodes: [] })
    },
    // Filter statements by source context (synthesis view).
    // Toggles: clicking the same context again clears the filter.
    setSelectedContextId: contextId => {
        const current = get().selectedContextId
        const newContextId = current === contextId ? null : contextId

        set({ selectedContextId: newContextId })

        // Re-apply highlight with the updated context filter
        const highlightedStatements = applyStatementHighlight(
            get().highlight,
            get().filteredStatements,
            get().pinnedNodes,
            newContextId
        )
        set({ filteredStatements: highlightedStatements })
    },
    setAvailableOptions: opts =>
        set(state => ({
            availableOptions: { ...state.availableOptions, ...opts },
        })),
    // Set selected statements (array or Set of statement IDs)
    setSelectedStatements: selected =>
        set(state => {
            const value =
                typeof selected === 'function'
                    ? selected(state.selectedStatements)
                    : selected
            return {
                selectedStatements: value,
            }
        }),
    resetSelectedStatements: () => set({ selectedStatements: new Set() }),

    setFilteredStatements: statements => {
        const currentRecalculateFilter = get().recalculate

        const availableOptions = get().recalculateAvailableOptions(
            statements,
            currentRecalculateFilter
        )
        let filteredStatements = statements
        if (!isFilterEmpty(currentRecalculateFilter)) {
            const filteredStatementsIds = get().filteredStatements.map(
                statement => statement.uid
            )
            filteredStatements = statements.filter(statement =>
                filteredStatementsIds.includes(statement.uid)
            )
        }
        // apply highlight filter if any
        const highlightedStatements = applyStatementHighlight(
            get().highlight,
            filteredStatements,
            get().pinnedNodes,
            get().selectedContextId
        )
        return set({
            filteredStatements: highlightedStatements,
            statements,
            availableOptions,
        })
    },

    // New: Calculate and set available options from filtered statements
    recalculateAvailableOptions: (statements, currentRecalculateFilter) => {
        const options = calculateAvailableOptions(
            statements,
            currentRecalculateFilter
        )
        return options
    },

    resetHighlightFilters: () =>
        set(() => ({
            highlight: {
                ...emptyState,
            },
        })),
    resetRecalculateFilters: () => {
        // Graph first so restoreOriginalTopics → deriveInsightClusters reads
        // the original graph's gaps (same rule as resetAllFilters).
        useGraphStore.getState().resetToOriginalGraph()
        get().restoreOriginalTopics()
        return set(() => ({
            recalculate: {
                ...emptyState,
            },
            stopWords: [],
            appliedStopWords: [],
            filteredStatements: get().statements,
        }))
    },
    // Distinct from `resetAllFilters`: skips the graph reset because the
    // search fetch sets new graph data immediately after this clears.
    resetForNewSearch: () =>
        set(() => ({
            recalculate: { ...emptyState },
            highlight: { ...emptyState },
            stopWords: [],
            appliedStopWords: [],
            selectedContextId: null,
            originalTopics: null,
        })),
    resetAllFilters: () => {
        // Graph first so restoreOriginalTopics → setTopics →
        // deriveInsightClusters reads the ORIGINAL graph's gaps, not the
        // still-filtered graph's (same ordering rule as the recalculation
        // callbacks above).
        useGraphStore.getState().resetToOriginalGraph()
        get().restoreOriginalTopics()
        const statements = get().statements
        const availableOptions = get().recalculateAvailableOptions(
            statements,
            emptyState
        )
        return set(() => ({
            recalculate: initialState.recalculate,
            highlight: initialState.highlight,
            stopWords: [],
            appliedStopWords: [],
            selectedContextId: null,
            filteredStatements: statements,
            availableOptions,
        }))
    },
    restoreOriginalTopics: () => {
        const originalTopics = get().originalTopics
        if (originalTopics && originalTopics.length > 0) {
            useZustand.getState().setTopics(originalTopics, true)
        }
        if (originalTopics) {
            const refreshedOptions = get().recalculateAvailableOptions(
                get().filteredStatements,
                get().recalculate
            )
            set({ originalTopics: null, availableOptions: refreshedOptions })
        }
    },

    // Utility selector
    isAnyFilterActive: () => {
        const activeFilterCount = get().activeFilterCount()
        return activeFilterCount > 0
    },
    isAnyHighlightActive: () => {
        const { highlight } = get()
        const activeHighlightCount =
            highlight.sentiments.length +
            highlight.tags.length +
            highlight.topics.length
        return activeHighlightCount > 0
    },
    isAnyRecalculateActive: () => {
        const { recalculate } = get()
        const activeRecalculateCount =
            recalculate.sentiments.length +
            recalculate.tags.length +
            recalculate.topics.length +
            (get().stopWords?.length || 0)
        return activeRecalculateCount > 0
    },
    // Check if only topic highlight is active (no other highlight filters)
    isOnlyTopicHighlightActive: () => {
        const { highlight } = get()
        return (
            highlight.topics.length > 0 &&
            highlight.sentiments.length === 0 &&
            highlight.tags.length === 0
        )
    },
    updateFilteredStatementsWithCommunities: () => {
        const statements = get().filteredStatements
        const updatedStatements = updateStatementCommunities(statements)

        const availableOptions = get().recalculateAvailableOptions(
            updatedStatements,
            get().recalculate
        )
        set({ filteredStatements: updatedStatements, availableOptions })
        return updatedStatements
    },
    activeFilterCount: () => {
        const { recalculate, highlight } = get()
        const activeRecalculateCount =
            recalculate.sentiments.length +
            recalculate.tags.length +
            recalculate.topics.length
        const activeHighlightCount =
            highlight.sentiments.length +
            highlight.tags.length +
            highlight.topics.length

        return activeRecalculateCount + activeHighlightCount
    },
}))
